import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { cva, VariantProps } from "class-variance-authority";
import * as React from "react";

const cardVariants = cva(
  "bg-surface-white flex flex-col gap-6 rounded-lg border border-edge-light p-6",
  {
    variants: {
      align: {
        center: "items-center justify-center text-center",
        start: "items-start justify-start text-left",
        between: "items-start justify-between",
      },
    },
    defaultVariants: {
      align: "center",
    },
  },
);

type CardProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardVariants>;

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, align, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ align }), className)}
      {...props}
    />
  ),
);

Card.displayName = "Card";

export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col items-center justify-start gap-6 self-stretch",
      className,
    )}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const cardTitleVariants = cva(
  "font-erode text-center text-[32px] font-normal capitalize leading-[35.2px] text-type-1",
  {
    variants: {
      variant: {
        default: "",
        "2": "font-archivo text-[18px] leading-[22px] md:text-[24px] md:leading-[26.4px] font-medium text-[#323232] wrap-break-word normal-case",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type CardTitleProps = React.HTMLAttributes<HTMLHeadingElement> &
  VariantProps<typeof cardTitleVariants>;

export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, variant, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(cardTitleVariants({ variant }), className)}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

const cardDescriptionVariants = cva(
  "self-stretch text-center font-archivo text-sm font-light leading-[23.8px] tracking-[0.14px] text-type-1",
  {
    variants: {
      variant: {
        default: "",
        "2": "text-base leading-[22px] md:text-[20px] md:leading-[28px] font-light text-[#323232] wrap-break-word tracking-normal",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type CardDescriptionProps = React.HTMLAttributes<HTMLParagraphElement> &
  VariantProps<typeof cardDescriptionVariants>;

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  CardDescriptionProps
>(({ className, variant, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(cardDescriptionVariants({ variant }), className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col items-center justify-start gap-3 self-stretch",
      className,
    )}
    {...props}
  />
));
CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-center gap-3 self-stretch",
      className,
    )}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export const CardActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col items-center justify-start gap-3 self-stretch",
      className,
    )}
    {...props}
  />
));
CardActions.displayName = "CardActions";

export const CardCTA = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", ...props }, ref) => (
    <Button
      ref={ref}
      size="sm"
      variant={variant}
      className={cn("w-full", className)}
      {...props}
    />
  ),
);
CardCTA.displayName = "CardCTA";
